import { Injectable, NgZone } from '@angular/core';

/**
 * Scroll / wheel guard for modals & CDK overlays (additive file).
 *
 * ---------------------------------------------------------------------------
 * PREVIOUS IMPLEMENTATION (kept verbatim below as reference; do not delete):
 * ---------------------------------------------------------------------------
 * - constructor: only wheel + touchmove listeners (passive: false, capture)
 * - shouldBlockBackgroundScroll: modal-open | app-main-scroll-locked | cdk-global-scrollblock
 * - isAllowedScrollTarget: reject backdrop; allow .modal.show, .cdk-overlay-pane, cdk-global-overlay-wrapper
 *
 * Issue: broad ".cdk-overlay-pane" still let scroll chain; body class can miss timing.
 * ---------------------------------------------------------------------------
 */
@Injectable({ providedIn: 'root' })
export class GlobalScrollBlockService {
  private scrollSnapshot = new Map<HTMLElement, number>();
  private freezeRaf: number | null = null;
  private wasBlocking = false;
  private savedWindowScrollY = 0;

  constructor(private readonly ngZone: NgZone) {
    if (typeof document === 'undefined') {
      return;
    }

    // --- previous listeners (unchanged behavior): capture wheel / touchmove ---
    document.addEventListener('wheel', this.onScrollLikeEvent, {
      passive: false,
      capture: true,
    });
    document.addEventListener('touchmove', this.onScrollLikeEvent, {
      passive: false,
      capture: true,
    });

    // --- added: watch lock signals + Material dialog in DOM; freeze nested scroll positions ---
    this.ngZone.runOutsideAngular(() => {
      const tick = () => this.syncFreezeState();
      const mo = new MutationObserver(tick);
      mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      mo.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      setTimeout(() => {
        const overlayHost = document.querySelector('.cdk-overlay-container');
        if (overlayHost) {
          mo.observe(overlayHost, { childList: true, subtree: true });
        }
      }, 0);
      tick();
    });
  }

  private readonly onScrollLikeEvent = (e: Event): void => {
    if (!this.shouldBlockBackgroundScroll()) {
      return;
    }
    const el = this.eventTargetElement(e);
    if (!el) {
      return;
    }
    if (this.isAllowedScrollTarget(el)) {
      return;
    }
    e.preventDefault();
  };

  /**
   * COMMENTED OUT (global): previously blocked background scroll for every modal/dialog.
   * Requirement: keep scroll freeze only for Mappings, Prevalidation, Lookup, Rule Builder.
   */
  // private shouldBlockBackgroundScroll(): boolean {
  //   const b = document.body;
  //   const html = document.documentElement;
  //   return (
  //     b.classList.contains('modal-open') ||
  //     b.classList.contains('app-main-scroll-locked') ||
  //     html.classList.contains('cdk-global-scrollblock') ||
  //     this.hasOpenMaterialDialogSurface()
  //   );
  // }

  /**
   * SCOPED (inline): freeze background scroll only for Mappings, Prevalidation, Lookup, Rule Builder.
   * Same allowlist as VSstyles.scss — no separate helper methods required.
   */
  private shouldBlockBackgroundScroll(): boolean {
    const html = document.documentElement;
    return !!(
      document.querySelector('.myCustomModalClasscreatexml.modal.show') ||
      document.querySelector('.myCustomModalClasslookup.modal.show') ||
      document.querySelector('.cdk-overlay-container .rule-config-dialog-container') ||
      html.classList.contains('cdk-global-scrollblock')
    );
  }

  // COMMENTED OUT: split into hasShellLockedBootstrapModal / hasShellLockedRuleBuilderDialog — logic inlined above.
  // private hasShellLockedBootstrapModal(): boolean { ... }
  // private hasShellLockedRuleBuilderDialog(): boolean { ... }

  private hasOpenMaterialDialogSurface(): boolean {
    return !!document.querySelector(
      '.cdk-overlay-container .mat-mdc-dialog-container, .cdk-overlay-container .mat-dialog-container'
    );
  }

  /**
   * Stricter than before: do NOT use the whole .cdk-overlay-pane — only concrete
   * scroll surfaces (dialog, menu, select, datepicker, bootstrap modal).
   */
  private isAllowedScrollTarget(el: Element): boolean {
    if (el.closest('.cdk-overlay-backdrop')) {
      return false;
    }
    if (el.closest('.modal.show')) {
      return true;
    }
    if (
      el.closest(
        '.mat-mdc-dialog-container, .mat-dialog-container, .mat-mdc-dialog-surface'
      )
    ) {
      return true;
    }
    if (el.closest('.mat-mdc-menu-panel')) {
      return true;
    }
    if (el.closest('.mat-mdc-select-panel')) {
      return true;
    }
    if (el.closest('.mat-datepicker-content')) {
      return true;
    }
    // ADDED: allow wheel/touch scroll on mat-autocomplete panels (e.g. Rule Builder Field dropdown) while MatDialog is open.
    if (el.closest('.mat-mdc-autocomplete-panel')) {
      return true;
    }
    return !!el.closest('.cdk-global-overlay-wrapper');
  }

  private eventTargetElement(e: Event): Element | null {
    const t = e.target;
    if (!t) {
      return null;
    }
    if (t instanceof Element) {
      return t;
    }
    if (t instanceof Node && t.parentElement) {
      return t.parentElement;
    }
    return null;
  }

  /** Added: snap background scroll positions every frame while blocked. */
  private syncFreezeState(): void {
    const block = this.shouldBlockBackgroundScroll();
    if (block && !this.wasBlocking) {
      this.captureScrollSnapshot();
      this.startFreezeLoop();
    } else if (!block && this.wasBlocking) {
      this.stopFreezeLoop();
      this.scrollSnapshot.clear();
    }
    this.wasBlocking = block;
  }

  private captureScrollSnapshot(): void {
    this.scrollSnapshot.clear();
    this.savedWindowScrollY =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    const overlay = document.querySelector('.cdk-overlay-container');
    const walk = (node: Element) => {
      if (overlay && overlay.contains(node)) {
        return;
      }
      if (node.closest?.('.modal.show')) {
        return;
      }
      const el = node as HTMLElement;
      if (typeof el.scrollTop !== 'number') {
        return;
      }
      const st = getComputedStyle(el);
      const oy = st.overflowY;
      const ox = st.overflowX;
      const scrollableY =
        (oy === 'auto' || oy === 'scroll' || st.overflow === 'auto' || st.overflow === 'scroll') &&
        el.scrollHeight > el.clientHeight + 1;
      const scrollableX =
        (ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth + 1;
      if (scrollableY || scrollableX) {
        this.scrollSnapshot.set(el, el.scrollTop);
      }
      for (const ch of Array.from(node.children)) {
        walk(ch as Element);
      }
    };
    if (document.body) {
      walk(document.body);
    }
  }

  private startFreezeLoop(): void {
    if (this.freezeRaf != null) {
      return;
    }
    const loop = () => {
      if (!this.shouldBlockBackgroundScroll()) {
        this.freezeRaf = null;
        return;
      }
      try {
        const wy =
          window.scrollY ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0;
        if (Math.abs(wy - this.savedWindowScrollY) > 0.5) {
          window.scrollTo(0, this.savedWindowScrollY);
        }
      } catch {
        /* ignore */
      }
      this.scrollSnapshot.forEach((top, el) => {
        try {
          if (el.isConnected && Math.abs(el.scrollTop - top) > 0.5) {
            el.scrollTop = top;
          }
        } catch {
          /* ignore */
        }
      });
      this.freezeRaf = requestAnimationFrame(loop);
    };
    this.freezeRaf = requestAnimationFrame(loop);
  }

  private stopFreezeLoop(): void {
    if (this.freezeRaf != null) {
      cancelAnimationFrame(this.freezeRaf);
      this.freezeRaf = null;
    }
  }
}

/*
ARCHIVED — exact `global-scroll-block.service.ts` before scroll-freeze + stricter overlay targets
(keep for rollback / diff; not compiled):

import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class GlobalScrollBlockService {
  constructor() {
    if (typeof document === 'undefined') {
      return;
    }
    document.addEventListener('wheel', this.onScrollLikeEvent, {
      passive: false,
      capture: true,
    });
    document.addEventListener('touchmove', this.onScrollLikeEvent, {
      passive: false,
      capture: true,
    });
  }

  private readonly onScrollLikeEvent = (e: Event): void => {
    if (!this.shouldBlockBackgroundScroll()) {
      return;
    }
    const el = this.eventTargetElement(e);
    if (!el) {
      return;
    }
    if (this.isAllowedScrollTarget(el)) {
      return;
    }
    e.preventDefault();
  };

  private shouldBlockBackgroundScroll(): boolean {
    const b = document.body;
    const html = document.documentElement;
    return (
      b.classList.contains('modal-open') ||
      b.classList.contains('app-main-scroll-locked') ||
      html.classList.contains('cdk-global-scrollblock')
    );
  }

  private isAllowedScrollTarget(el: Element): boolean {
    if (el.closest('.cdk-overlay-backdrop')) {
      return false;
    }
    if (el.closest('.modal.show')) {
      return true;
    }
    if (el.closest('.cdk-overlay-pane')) {
      return true;
    }
    return !!el.closest('.cdk-global-overlay-wrapper');
  }

  private eventTargetElement(e: Event): Element | null {
    const t = e.target;
    if (!t) {
      return null;
    }
    if (t instanceof Element) {
      return t;
    }
    if (t instanceof Node && t.parentElement) {
      return t.parentElement;
    }
    return null;
  }
}
*/
